import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authService } from '../services/api';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [realUser, setRealUser] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  // The effective user the rest of the app sees — everywhere else in the
  // app should read this, not realUser, so "View as Teacher/Student"
  // transparently swaps every role-gated screen without each one needing
  // to know about impersonation.
  const currentUser = impersonating || realUser;
  const [authLoading, setAuthLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const dismissTimer = useRef(null);

  const dismissNotification = useCallback(() => {
    clearTimeout(dismissTimer.current);
    setNotification(null);
  }, []);

  const notify = useCallback((message, type = 'info') => {
    clearTimeout(dismissTimer.current);
    setNotification({ message, type });
    dismissTimer.current = setTimeout(() => setNotification(null), 4000);
  }, []);

  useEffect(() => {
    // A page refresh always comes back as the real user — impersonation
    // is tab-session-only, never carried across a reload.
    sessionStorage.removeItem('impersonatingId');
    sessionStorage.removeItem('impersonatingRole');
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    authService
      .me()
      .then(setRealUser)
      .catch(() => localStorage.removeItem('token'))
      .finally(() => setAuthLoading(false));
  }, []);

  const login = async (identifier, password) => {
    const { token, user } = await authService.login(identifier, password);
    localStorage.setItem('token', token);
    setRealUser(user);
    return user;
  };

  const loginWithGoogle = async (credential) => {
    const { token, user } = await authService.google(credential);
    localStorage.setItem('token', token);
    setRealUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('impersonatingId');
    sessionStorage.removeItem('impersonatingRole');
    setImpersonating(null);
    setRealUser(null);
  };

  // Only a super admin can do this (also enforced server-side) — the
  // caller already has the target StaffAccount/Customer object (e.g.
  // from the Staff or Students page's list), so no extra fetch is
  // needed to "become" them. role is 'teacher', 'admin', or 'student'.
  const startImpersonation = (target, role = 'teacher') => {
    sessionStorage.setItem('impersonatingId', target.id);
    sessionStorage.setItem('impersonatingRole', role);
    setImpersonating({ id: target.id, name: target.name, role });
  };

  const stopImpersonation = () => {
    sessionStorage.removeItem('impersonatingId');
    sessionStorage.removeItem('impersonatingRole');
    setImpersonating(null);
  };

  const value = {
    currentUser,
    realUser,
    impersonating,
    startImpersonation,
    stopImpersonation,
    authLoading,
    login,
    loginWithGoogle,
    logout,
    notification,
    notify,
    dismissNotification,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
};
